/**
 * Permission codes the Organization screens declare an interest in.
 *
 * They are the dot-strings `internal/org/permissions.go` registers with the RBAC
 * catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. They live beside the
 * module rather than in `@/lib/permissions` only because that file belongs to
 * another workstream this session; folding them in is a one-line move and is
 * noted for the orchestrator.
 *
 * `periodClose` is deliberately spelled `gl.period.close`: the fiscal_periods
 * table lives in org, but §7 names the permission for the ledger and every
 * module from M3 onward checks for exactly that string.
 */

export const ORG_PERMISSIONS = {
  companyRead: 'org.company.read',
  companyCreate: 'org.company.create',
  companyUpdate: 'org.company.update',
  companyDelete: 'org.company.delete',

  unitRead: 'org.unit.read',
  unitCreate: 'org.unit.create',
  unitUpdate: 'org.unit.update',
  unitDelete: 'org.unit.delete',

  fiscalRead: 'org.fiscal.read',
  fiscalManage: 'org.fiscal.manage',
  /** Close and reopen. Marked `requiresMfa` in the catalogue — see §6 step-up. */
  periodClose: 'gl.period.close',

  accountRead: 'org.account.read',
  accountCreate: 'org.account.create',
  accountUpdate: 'org.account.update',

  templateRead: 'org.coa_template.read',
  templateManage: 'org.coa_template.manage',

  openingBalanceRead: 'org.opening_balance.read',
  openingBalanceImport: 'org.opening_balance.import',
  openingBalanceSubmit: 'org.opening_balance.submit',

  /**
   * Post an approved opening-balance import as the opening journal.
   *
   * Spelled `gl.journal.post` because that is what guards
   * `POST /opening-balances/:id/post` — the route lives in `internal/gl`, and
   * writing the ledger is the ledger's authority, not org's. It is deliberately
   * NOT `org.opening_balance.submit`: `core/rbac` declares
   * `gl.journal.create` ⊗ `gl.journal.post` a segregation-of-duties pair, so the
   * hands that raise the proposal and the hands that put it in the ledger are
   * separated by construction. The permission carries no `RequiresMFA` in the
   * catalogue, so there is no step-up to anticipate here; if the server ever
   * adds one it arrives as `ERR_STEP_UP_REQUIRED` and is rendered as the server
   * worded it.
   */
  journalPost: 'gl.journal.post',

  checklistRead: 'org.checklist.read',

  /** Read the currency list to offer a company's base currency as a choice. */
  currencyRead: 'fx.currency.read',

  /* ------------------------------------------------------- C1 additions */

  /**
   * The C1 enterprise-foundation codes (`internal/org/permissions.go`, design
   * §6.1). Every one of them splits read from manage, and every screen route in
   * `ORG_C1_ROUTES` is gated on the *read* half: an Auditor holds only the read
   * codes and must be able to open a location, a position and the filing
   * calendar and change none of them.
   *
   * `periodPermanentClose` is spelled for the ledger (`gl.period.*`) for the
   * same reason `periodClose` above is: the table lives in org, the authority is
   * the ledger's. It carries step-up MFA server-side — the fourth period state
   * is terminal, and §6.7 gives it its own error code precisely so a screen can
   * refuse to offer "ask for a reopen" on it.
   */
  locationRead: 'org.location.read',
  locationManage: 'org.location.manage',

  divisionRead: 'org.division.read',
  divisionManage: 'org.division.manage',

  ledgerRead: 'org.ledger.read',

  designationRead: 'org.designation.read',
  designationManage: 'org.designation.manage',

  filingRead: 'org.filing.read',
  filingManage: 'org.filing.manage',

  jobRead: 'org.job.read',
  jobManage: 'org.job.manage',

  positionRead: 'org.position.read',
  positionManage: 'org.position.manage',

  assignmentRead: 'org.assignment.read',
  assignmentManage: 'org.assignment.manage',

  /** Step-up MFA server-side; the terminal period state. */
  periodPermanentClose: 'gl.period.permanent_close',

  /** Reason codes are read to fill every governed reason picker in this module. */
  reasonCodeRead: 'rules.reason.read',
} as const

export type OrgPermission = (typeof ORG_PERMISSIONS)[keyof typeof ORG_PERMISSIONS]
